/**
 * The Command Desk's arithmetic — every figure the page prints, and nothing that draws.
 *
 * Spec `01-command-desk.md` §1 is emphatic that this module owns no primary data: *"Everything
 * on it is derived — from the TMS read layer, the appointments store, the officials store or
 * the mailbox monitor."* That makes the page almost entirely composition, and composition is
 * the part worth testing on its own. Nothing below fetches, and nothing below renders.
 *
 * **Dates are calendar days, not instants.** A match day is a day in the host city, so every
 * date here is parsed and read back in UTC — the same discipline `FixturesPage` established.
 * A laptop in a different zone must not be able to move a fixture into yesterday.
 *
 * **RUN 82G — and the clock is the host city's too.** Which day it is (`todayIso`) and how long
 * there is to a kick-off (`countdown`) are read on the tournament's clock, Asia/Tokyo, through
 * `Intl` — never on the zone the laptop happens to be set to. Every played rule below already read
 * Tokyo's clock; until RUN 82G those two did not, so the desk agreed with itself only near UTC+9.
 *
 * **Fictional data only** anywhere this file is exercised; it invents no names of its own.
 */

import type {
  OfficialWorkload,
  TechnicalSlot,
  TmsMatchStatus,
  UmpireWorkload,
} from "@hockeytd/shared";
import { MATCH_LENGTH_MINUTES, TECHNICAL_SLOTS } from "@hockeytd/shared";

import type {
  AppointmentFixtureBody,
  AppointmentSlotBody,
  FixtureRowView,
  HearingCaseView,
  OfficialView,
  UmpireFixtureBody,
} from "../../api/types.js";
import {
  tournamentClockParts,
  tournamentDay,
  tournamentDayAndTime,
} from "../../app/tournamentTime.js";
import { matchHasLikelyEnded, tokyoClockTime } from "../../pages/um/matchDebrief.js";
import { countOf } from "../appointments/vocabulary.js";

/* ────────────────────────────────────────────────────────────────────────────
 * 1. Calendar
 * ──────────────────────────────────────────────────────────────────────────── */

const WEEKDAYS_LONG = [
  "Sunday",
  "Monday",
  "Tuesday",
  "Wednesday",
  "Thursday",
  "Friday",
  "Saturday",
];
const WEEKDAYS_SHORT = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];
const MONTHS_LONG = [
  "January",
  "February",
  "March",
  "April",
  "May",
  "June",
  "July",
  "August",
  "September",
  "October",
  "November",
  "December",
];
const MONTHS_SHORT = [
  "Jan",
  "Feb",
  "Mar",
  "Apr",
  "May",
  "Jun",
  "Jul",
  "Aug",
  "Sep",
  "Oct",
  "Nov",
  "Dec",
];

function utc(iso: string): Date | null {
  const date = new Date(`${iso}T00:00:00Z`);
  return Number.isNaN(date.getTime()) ? null : date;
}

/** `2026-09-18` → `Friday 18 September` — §5's dateline heading. */
export function longDate(iso: string): string {
  const date = utc(iso);
  if (date === null) return iso;
  return `${WEEKDAYS_LONG[date.getUTCDay()] ?? ""} ${String(date.getUTCDate())} ${
    MONTHS_LONG[date.getUTCMonth()] ?? ""
  }`;
}

/** `2026-09-18` → `Fri 18 Sep` — the panel notes and the day-note variants. */
export function shortDate(iso: string): string {
  const date = utc(iso);
  if (date === null) return iso;
  return `${WEEKDAYS_SHORT[date.getUTCDay()] ?? ""} ${String(date.getUTCDate())} ${
    MONTHS_SHORT[date.getUTCMonth()] ?? ""
  }`;
}

/** `2026-09-18` → `18 Sep`, for the hero kicker's window and the day strip's aria labels. */
export function dayMonth(iso: string): string {
  const date = utc(iso);
  if (date === null) return iso;
  return `${String(date.getUTCDate())} ${MONTHS_SHORT[date.getUTCMonth()] ?? ""}`;
}

/**
 * RUN 82G's tournament clock — **RUN 83 moved it out of this file.**
 *
 * The second independent QA of the desk (M1) found `todayIso` and `countdown` reading the DEVICE's
 * zone while every played rule on the page reads Tokyo's. On a laptop set to UTC a Tokyo morning
 * was still yesterday — the focus day, the day strip's past shading and the hero all stood a day
 * behind the `FT` pills beside them — and the desk's own tests passed only on a machine set near
 * UTC+9. `Intl` knows what Asia/Tokyo's clock read at any instant, so the desk asked it.
 *
 * RUN 83's sweep found nine more surfaces with the same defect, so the reading itself now lives in
 * `app/tournamentTime.ts` and every one of them asks that. Nothing about what the desk computes
 * has changed, and nothing about what it exports has either: both names are still exported from
 * here, with the same signatures, so no import and no test in this feature names a moved symbol
 * through a new path — the same way run 75 lifted the debrief selectors.
 */
export { tournamentDayAndTime };

/**
 * Today on the tournament's clock, in the `YYYY-MM-DD` shape a fixture carries.
 *
 * The default is what makes this `todayIso` rather than `tournamentDay`: on this page "today"
 * genuinely means now unless a test says otherwise, and that default is the codebase's pattern
 * for a testable clock. The shared module never reads the clock itself.
 */
export function todayIso(now: Date = new Date()): string {
  return tournamentDay(now);
}

/** The day `count` days after `iso`, in the same shape. */
export function addDays(iso: string, count: number): string {
  const date = utc(iso);
  if (date === null) return iso;
  date.setUTCDate(date.getUTCDate() + count);
  return date.toISOString().slice(0, 10);
}

/** Whole days from `from` to `to`; negative when `to` is behind. */
export function daysBetween(from: string, to: string): number {
  const a = utc(from);
  const b = utc(to);
  if (a === null || b === null) return 0;
  return Math.round((b.getTime() - a.getTime()) / 86_400_000);
}

/* ────────────────────────────────────────────────────────────────────────────
 * 2. Which day the desk is looking at
 * ──────────────────────────────────────────────────────────────────────────── */

/** The distinct days the schedule names, in order. */
export function matchDays(fixtures: readonly FixtureRowView[]): string[] {
  return [...new Set(fixtures.map((fixture) => fixture.date))].sort();
}

export interface FocusDay {
  /** The day `Today's Matches` is drawn for. Null when the schedule holds no fixture at all. */
  day: string | null;
  /** True when that day IS today; false when the desk has fallen forward to the next one. */
  isToday: boolean;
  /** `Matchday N of M`, 1-based over the days that hold fixtures. Null when there is no day. */
  index: number | null;
  total: number;
}

/**
 * §15's `Day 0 / pre-tournament` state, and its mirror at the other end.
 *
 * Today wins whenever the schedule holds a fixture for it. Otherwise the desk falls forward
 * to the next day that does — which is what makes the page useful on a rest day and on the
 * morning before the tournament starts — and says so, rather than drawing an empty table
 * under today's date and leaving the TD to work out that nothing is wrong.
 */
export function focusDay(fixtures: readonly FixtureRowView[], today: string): FocusDay {
  const days = matchDays(fixtures);
  if (days.length === 0) return { day: null, isToday: false, index: null, total: 0 };
  const chosen = days.includes(today)
    ? today
    : (days.find((day) => day > today) ?? days[days.length - 1] ?? null);
  if (chosen === null) return { day: null, isToday: false, index: null, total: days.length };
  return {
    day: chosen,
    isToday: chosen === today,
    index: days.indexOf(chosen) + 1,
    total: days.length,
  };
}

/**
 * RUN 82J — the match day after `day` in the whole schedule; null after the last one, or with no
 * day to start from.
 *
 * The third independent QA of the desk (L5, 2026-09-18) found the rail's Appointment Status
 * following the lookahead's pager: its window was the days whose appointments the desk happened to
 * hold, and a page turn changed that. The window is now two fixed days — the focus day and this
 * one — and this one is read off the schedule alone: not the lookahead's page and not its
 * competition filter, so the pager and the tabs cannot move it.
 */
export function nextMatchDay(
  fixtures: readonly FixtureRowView[],
  day: string | null,
): string | null {
  if (day === null) return null;
  return matchDays(fixtures).find((candidate) => candidate > day) ?? null;
}

/** Sort order everywhere on this page: day, then kick-off, then Men's before Women's. */
export function byKickOff(a: FixtureRowView, b: FixtureRowView): number {
  if (a.date !== b.date) return a.date < b.date ? -1 : 1;
  if (a.time !== b.time) return a.time < b.time ? -1 : 1;
  if (a.competitionGender !== b.competitionGender) {
    return a.competitionGender < b.competitionGender ? -1 : 1;
  }
  return a.displayMatchNo < b.displayMatchNo ? -1 : 1;
}

/* ────────────────────────────────────────────────────────────────────────────
 * 2a. Whether a match has been played — the desk's one rule (RUN 82C)
 * ──────────────────────────────────────────────────────────────────────────── */

/**
 * RUN 82C — how long an explicit `live` or `warmup` status holds a match open past its scheduled
 * kick-off: **150 minutes**, fifty past `MATCH_LENGTH_MINUTES`.
 *
 * The hundred minutes are the honest approximation of a match with no Full Time signal (see
 * `match-clock.ts`). They are also exactly the margin a delayed start or a shoot-out eats, and
 * those are the matches TMS is most likely to still be calling live when the hundred minutes
 * are up. A match TMS says is on is on — for fifty minutes more. Past that, a `live` that was
 * never moved to `completed` is the same stale status as a `published` one, and the clock wins
 * again, so no match can be held open for the rest of the tournament by a status nobody moved.
 */
export const LIVE_STATUS_HOLD_MINUTES = 150;

/**
 * Whether a match has been played — **the one rule every figure on the Command Desk reads.**
 *
 * RUN 82 wrote this for §8b's locked record alone. The independent QA of that run (RUN 82C)
 * found the desk then held two rules for the same question: this one, and a status-only reading
 * everywhere else — the dateline's `Played`, the `FT` pill, §10's `completed` half, Pool Stage
 * Progress, the hero's next push-back and the unfilled-slot alerts. On the live system the two
 * disagree all day, because the status stays `published` through play: the tally counted a match
 * the §10 cell beside it said was not played. So the rule lives here, once, and every one of
 * those reads it (decision 1 of the RUN 82C addendum, made for the Technical Delegate, who may
 * still overrule it before a deploy).
 *
 * **It is not the schedule's status alone, and it must not be.** The fixture status this system
 * mirrors is not moved during play — on the live system a match stays `published` long after full
 * time — so a rule that waited for `completed` would leave every one of those figures where it
 * stood at the first push-back. The rule is the one the Control Centre already uses to decide a
 * match is over, on the Fixtures page's `Debrief ›` link (RUN 75): TMS says the match is finished,
 * **or** the tournament clock is past its scheduled kick-off plus `MATCH_LENGTH_MINUTES` —
 * `matchHasLikelyEnded`, the UM app's own reckoning (spec 13 §3.2), reused rather than re-derived,
 * so the arithmetic of a kick-off on Tokyo's clock plus the match length is written once.
 *
 * **RUN 82G — where the desk and those pages still differ (second QA, L9).** This note used to say
 * that reusing the helper meant the desk, the Fixtures page and the UM app "can never disagree"
 * about when a match ended. They can, for fifty minutes. The Fixtures page's `Debrief ›` link
 * (`FixturesPage`'s row) and the UM app's debrief list (`UmTodayPage`'s `debriefRows`) call
 * `matchHasLikelyEnded` directly, without the `live` / `warmup` hold below — so a match TMS still
 * calls live between kick-off plus 100 and kick-off plus 150 is over on those two pages and still
 * under way on this one. Those pages were not changed in RUN 82G: whether they take the hold too
 * is a decision about them, not a correction to this rule.
 *
 * **Except that TMS's `live` or `warmup` wins until kick-off plus {@link LIVE_STATUS_HOLD_MINUTES}.**
 * A match still showing LIVE at the hundred-minute mark is a late start or a shoot-out, not a
 * finished match, and counting it played would print `FT` beside TMS's own `LIVE`. The hold is
 * measured by the same helper on a clock set back by the difference, so the Tokyo reading of the
 * kick-off, and the answer for one that cannot be parsed, stay exactly the helper's.
 *
 * That helper answers `true` for a kick-off it cannot parse, which is its stated generous branch.
 * The fixture record carries its kick-off as a time of day and never without one, so on real data
 * the branch is not reached; were it reached, a match would read as finished rather than stay
 * open for ever.
 */
export function matchCompleted(
  fixture: Pick<FixtureRowView, "date" | "time">,
  tmsStatus: TmsMatchStatus | null,
  now: Date,
): boolean {
  if (tmsStatus === "completed" || tmsStatus === "official") return true;
  if (tmsStatus === "live" || tmsStatus === "warmup") {
    const holdBeyondMatch = (LIVE_STATUS_HOLD_MINUTES - MATCH_LENGTH_MINUTES) * 60_000;
    return matchHasLikelyEnded(fixture, new Date(now.getTime() - holdBeyondMatch));
  }
  return matchHasLikelyEnded(fixture, now);
}

/**
 * RUN 82G — whether a match is being played now: **not yet played, and either TMS calls it `live`
 * or the tournament clock is at or past its scheduled kick-off.**
 *
 * The second independent QA of the desk (M2) found the other half of the problem {@link
 * matchCompleted} solved. The dateline's `Live`, the crew glance's `In progress` and the live row's
 * styling all waited for TMS's own `live` — and the status this system mirrors is not moved during
 * play, so on the live system a match being played sat under `To Come` for its hundred minutes,
 * beside a hero already saying `Under way` by the clock, and then jumped straight to `FT`. The
 * decision (the orchestrator's, consistent with the one played rule; the Technical Delegate may
 * veto it) is the same shape as that rule: TMS's word, or the clock.
 *
 * It is asked of a match {@link matchCompleted} has not called played, so the two are never true
 * together, and a `live` status past its hold is played rather than under way. Kick-off is read
 * the way the played rule reads it — `matchHasLikelyEnded`, on a clock moved on by the match length
 * — so the Tokyo reading of the kick-off, and the answer for one that cannot be parsed (played,
 * and therefore not under way), stay exactly that helper's.
 *
 * **What it does NOT drive.** The `LIVE` chip beside the match number and the status pill's period
 * (`Q3`, `HT`) are still TMS's own `live` and nothing else: they print what TMS says the match is
 * doing, and a clock can say that a match has started but not which quarter it is in.
 */
export function matchUnderWay(
  fixture: Pick<FixtureRowView, "date" | "time">,
  tmsStatus: TmsMatchStatus | null,
  now: Date,
): boolean {
  if (matchCompleted(fixture, tmsStatus, now)) return false;
  if (tmsStatus === "live") return true;
  return matchHasLikelyEnded(fixture, new Date(now.getTime() + MATCH_LENGTH_MINUTES * 60_000));
}

/* ────────────────────────────────────────────────────────────────────────────
 * 3. The dateline's tally
 * ──────────────────────────────────────────────────────────────────────────── */

export interface DayTally {
  total: number;
  played: number;
  live: number;
  toCome: number;
}

/**
 * §5: `Showing 6 Matches Today · 2 Played · 1 Live · 3 To Come`. All computed.
 *
 * **RUN 82C — `Played` is the desk's one rule ({@link matchCompleted}), not the status alone.**
 * `Live` is still TMS's own word, and it is counted only while the rule has not yet called the
 * match played — so the three figures are three sets that never overlap, and they always add up
 * to the day's total.
 *
 * **RUN 82G — `Live` is {@link matchUnderWay}, no longer TMS's word alone.** A match whose status
 * the mirror never moved counted `To Come` for the whole of its hundred minutes. It is `Live` from
 * its kick-off now, and `Played` from the moment the played rule says so; `matchUnderWay` is never
 * true of a played match, so the three sets still never overlap and still add up.
 */
export function tallyDay(dayFixtures: readonly FixtureRowView[], now: Date): DayTally {
  const played = dayFixtures.filter((fixture) =>
    matchCompleted(fixture, fixture.tmsStatus, now),
  ).length;
  const live = dayFixtures.filter((fixture) =>
    matchUnderWay(fixture, fixture.tmsStatus, now),
  ).length;
  return {
    total: dayFixtures.length,
    played,
    live,
    toCome: dayFixtures.length - played - live,
  };
}

/* ────────────────────────────────────────────────────────────────────────────
 * 4. The crew sheet — §6
 * ──────────────────────────────────────────────────────────────────────────── */

/**
 * §6 fixes the seven slots and their order. Three of them have a store in this build.
 *
 * `TO`, `SJ` and `TJ` are the technical crew of module 01, which is built and which the
 * appointments store holds. `U1`, `U2`, `RU` and `VU` are Umpire Appointments — module 02,
 * which is **not built** — so there is nowhere for them to come from. They keep their place
 * in the sheet, because a sheet missing four of its seven boxes would misread as a crew of
 * three, and each says which module owns it rather than showing `Awaiting UM`, which would
 * assert that an Umpires Manager has been asked and has not yet answered.
 */
export const CREW_SLOTS = ["TO", "SJ", "TJ", "U1", "U2", "RU", "VU"] as const;
export type CrewSlot = (typeof CREW_SLOTS)[number];

/** The four this build has no store for. */
export const UMPIRING_SLOTS: readonly CrewSlot[] = ["U1", "U2", "RU", "VU"];

export function isTechnicalSlot(slot: CrewSlot): slot is TechnicalSlot {
  return (TECHNICAL_SLOTS as readonly string[]).includes(slot);
}

/**
 * RUN 82J — where one day's own `GET /appointments?day=` stands: `read` (its answer is held),
 * still `loading` (asked, and never yet answered), or `failed` (refused, and never answered).
 *
 * The third independent QA of the desk (M3, 2026-09-18) found a day nobody had read drawn as a day
 * nobody had appointed: `TO, SJ, TJ Pending` and `Scheduled` on Today's Matches, and every match
 * counted incomplete by Appointment Status, for as long as the read was out or refused. An empty
 * index cannot tell "nobody is appointed" from "nothing has been read", so the figures that read the
 * crews are handed this beside the index, and say nothing about a crew until it is `read`. A refresh
 * that fails after an answer landed keeps that answer (`useRefreshingAsync`), so it stays `read`.
 */
export type DayReadState = "read" | "loading" | "failed";

/**
 * RUN 82J's two words for a day whose appointments are not held, lifted out of {@link crewAtAGlance}
 * in RUN 82T (final delta QA, H-1, 2026-09-18) so that both match panels say them in one voice.
 *
 * `Today's Matches` says them in its crew glance, a row at a time. `Tomorrow Onwards` has no crew
 * column to say them in — its fifth cell is the stage — so it says them once on the day header the
 * rows are grouped under, which is the same fact about the same day. Two panels, two places, one
 * pair of words; before RUN 82T the second panel said nothing at all and drew `Scheduled`.
 *
 * Null for a day that HAS been read: there is nothing to say, and the rows state its crews.
 */
export function crewReadNote(crews: DayReadState): string | null {
  if (crews === "read") return null;
  return crews === "loading" ? "Crew loading" : "Crew not loaded";
}

/**
 * RUN 82T — where ONE of `Tomorrow Onwards`' days stands, from everything the page knows about that
 * day (final delta QA, H-1, 2026-09-18).
 *
 * The lookahead learns a day three ways, and they have to be asked in one order:
 *
 *   · **A body held wins.** The page's own read for that day has answered, or a sheet press fetched
 *     it. Either way the desk holds the day's appointments and its rows may state them.
 *   · **A sheet press still out is `loading`.** The day's page read was refused and a press has
 *     asked again, so the sheet says `Loading` rather than the `Not loaded` it said a moment before.
 *   · **Otherwise the day's own read says** — `loading` while it is out, `failed` once refused.
 *
 * A day nothing has asked about is `loading` and never `read`: `read` is a claim that the desk holds
 * that day's appointments, and nothing that has not been read may make it. (No rendered row is such
 * a day — every lookahead row is on a day of the page, and the page reads each of its days — but the
 * fallback is what stops a day the page stopped covering from reading as a day nobody has appointed,
 * which is the whole of H-1.)
 */
export function lookaheadDayRead(
  day: string,
  held: ReadonlySet<string>,
  pending: ReadonlySet<string>,
  reads: ReadonlyMap<string, DayReadState>,
): DayReadState {
  if (held.has(day)) return "read";
  if (pending.has(day)) return "loading";
  return reads.get(day) ?? "loading";
}

export interface CrewCell {
  slot: CrewSlot;
  /**
   * `filled` · `unfilled` · `no_store` — the third is module 02, not an empty appointment.
   * RUN 82J — `unread`: a technical slot on a day whose appointments have not been read, which
   * says so rather than `Unfilled` (M3).
   */
  state: "filled" | "unfilled" | "no_store" | "unread";
  tmsPersonId: string | null;
  name: string;
  /** `MAS · ATD`, or `—`. */
  nat: string;
}

/**
 * One fixture's seven boxes, in §6's fixed order.
 *
 * RUN 82J — `crews` is where the day's appointments read stands (M3). Until it is `read`, the three
 * technical boxes say `Loading` or `Not loaded` rather than `Unfilled`, which would be a statement
 * about appointments nobody has read. The four umpiring boxes are the same either way.
 */
export function crewSheet(
  appointed: AppointmentFixtureBody | null,
  crews: DayReadState = "read",
): CrewCell[] {
  return CREW_SLOTS.map((slot): CrewCell => {
    if (!isTechnicalSlot(slot)) {
      return {
        slot,
        state: "no_store",
        tmsPersonId: null,
        name: "Not appointed here",
        nat: "—",
      };
    }
    if (crews !== "read") {
      return {
        slot,
        state: "unread",
        tmsPersonId: null,
        name: crews === "loading" ? "Loading" : "Not loaded",
        nat: "—",
      };
    }
    const filled = appointed?.slots.find(
      (entry) => entry.slot === slot && entry.tmsPersonId !== null,
    );
    if (filled === undefined || filled.tmsPersonId === null) {
      return { slot, state: "unfilled", tmsPersonId: null, name: "Unfilled", nat: "—" };
    }
    const role = filled.role === null || filled.role === "TO" ? "" : ` · ${filled.role}`;
    return {
      slot,
      state: "filled",
      tmsPersonId: filled.tmsPersonId,
      name: filled.mediaName ?? filled.tmsPersonId,
      nat: `${filled.country ?? "—"}${role}`,
    };
  });
}

/** How many of the three technical slots hold somebody. */
export function technicalFilled(appointed: AppointmentFixtureBody | null): number {
  if (appointed === null) return 0;
  return appointed.slots.filter(
    (slot) => isTechnicalSlot(slot.slot) && slot.tmsPersonId !== null,
  ).length;
}

/** True when all three technical slots are filled — the only completeness this build can state. */
export function technicalComplete(appointed: AppointmentFixtureBody | null): boolean {
  return technicalFilled(appointed) === TECHNICAL_SLOTS.length;
}

/** Any slot on this fixture the rule pipeline has flagged as the official's own country. */
export function hasCountryConflict(appointed: AppointmentFixtureBody | null): boolean {
  return appointed?.slots.some((slot) => slot.countryConflict) ?? false;
}

/**
 * §6's `Crew at a glance` — `Pool B · TO OKONKWO Ada · Technical crew confirmed`.
 *
 * The third clause follows the match when the match has moved: a completed match's crew is
 * not news, and §6 prints `Match completed` / `In progress` there instead.
 *
 * **RUN 82 (§6a).** The desk no longer prints `to` in this line: the TO now heads the crew line
 * drawn under it ({@link crewLine}), beside the SJ and TJ. `to` is still returned, unchanged.
 *
 * **RUN 82C.** `Match completed` follows the desk's one played rule ({@link matchCompleted}), and
 * `In progress` is said only of a match that rule has not yet called played. And a full crew is
 * no longer `confirmed` merely for being full: the clause says what the crew IS. `Technical crew
 * confirmed` when it is signed off or locked; `Reopened for correction` when it was unlocked and
 * owes TMS a re-push; `Technical crew drafted` otherwise. The sign-off is asked first, as the
 * independent QA put it: `Unlock` clears the confirmation (§A11), so a reopened match reads
 * `Reopened for correction` until the correction is signed off again — and from then until the
 * day is re-locked it reads `Technical crew confirmed` beside a `Needs Re-Push` chip, which are
 * two different facts and both true.
 *
 * **RUN 82G.** `In progress` is said of a match {@link matchUnderWay} — TMS's `live`, or the clock
 * at or past kick-off — where it waited for TMS's `live` alone, which the mirrored status never
 * reaches during play.
 *
 * **RUN 82J — nothing about the crew before the day's crews are read (third QA, M3).** While `crews`
 * is `loading` the clause is `Crew loading`, and once the read has been refused it is `Crew not
 * loaded` — never `TO, SJ, TJ Pending`, which an empty index would otherwise say of every match on a
 * day nobody has read. `Match completed` and `In progress` are the match's own state, not the crew's,
 * and are still said.
 */
export function crewAtAGlance(
  fixture: FixtureRowView,
  appointed: AppointmentFixtureBody | null,
  now: Date,
  crews: DayReadState = "read",
): { stage: string; to: string | null; note: string; noteIsWarning: boolean } {
  const to = appointed?.slots.find((slot) => slot.slot === "TO" && slot.tmsPersonId !== null);
  if (matchCompleted(fixture, fixture.tmsStatus, now)) {
    return { stage: fixture.stage, to: to?.mediaName ?? null, note: "Match completed", noteIsWarning: false };
  }
  if (matchUnderWay(fixture, fixture.tmsStatus, now)) {
    return { stage: fixture.stage, to: to?.mediaName ?? null, note: "In progress", noteIsWarning: false };
  }
  const unread = crewReadNote(crews);
  if (unread !== null) {
    // RUN 82T — through `crewReadNote`, which the lookahead's day headers say too (H-1).
    return { stage: fixture.stage, to: null, note: unread, noteIsWarning: false };
  }
  const missing = TECHNICAL_SLOTS.filter(
    (slot) =>
      appointed?.slots.find((entry) => entry.slot === slot && entry.tmsPersonId !== null) ===
      undefined,
  );
  if (missing.length > 0) {
    return {
      stage: fixture.stage,
      to: to?.mediaName ?? null,
      note: `${missing.join(", ")} Pending`,
      noteIsWarning: true,
    };
  }
  return {
    stage: fixture.stage,
    to: to?.mediaName ?? null,
    note:
      appointed !== null && (appointed.confirmed || appointed.locked)
        ? "Technical crew confirmed"
        : appointed?.needsPush === true
          ? "Reopened for correction"
          : "Technical crew drafted",
    noteIsWarning: false,
  };
}

/* ────────────────────────────────────────────────────────────────────────────
 * 5. The status pill — §6 and §7's `statusFor`
 * ──────────────────────────────────────────────────────────────────────────── */

export interface DeskStatus {
  variant: "good" | "warn" | "alert" | "muted" | "live";
  label: string;
}

/**
 * §7's display contract, held exactly: `Conflict` when a rule fails · `Appointing` when any
 * slot is draft or unfilled · `Appointed` when the full crew is confirmed · `Scheduled` when
 * nothing is drafted yet. §6 adds the two match states that outrank all four — a finished
 * match is `FT` and a live one shows its period.
 *
 * **RUN 82C — `finished` is the desk's one played rule ({@link matchCompleted}).** A match the
 * clock has finished is `FT` though its mirrored status still says `published`, and a `live`
 * status past its hold is `FT` too — which is also why it is asked first.
 *
 * **RUN 82G — the period pill is still TMS's `live` alone.** A match the clock says is under way
 * ({@link matchUnderWay}) but TMS does not call live keeps its appointment pill: the pill prints
 * TMS's period (`Q3`, `HT`), and no clock knows which quarter a match is in.
 *
 * **RUN 82J.** The two match states are {@link matchOwnStatus} now, so that a row whose crews have
 * not been read can draw them and nothing else (M3). The order of the questions is unchanged.
 */
export function deskStatus(
  fixture: FixtureRowView,
  appointed: AppointmentFixtureBody | null,
  now: Date,
): DeskStatus {
  const own = matchOwnStatus(fixture, now);
  if (own !== null) return own;
  if (hasCountryConflict(appointed)) return { variant: "alert", label: "Conflict" };
  const filled = technicalFilled(appointed);
  if (filled === 0) return { variant: "muted", label: "Scheduled" };
  if (filled < TECHNICAL_SLOTS.length) return { variant: "warn", label: "Appointing" };
  const anyDraft =
    appointed?.slots.some(
      (slot) => slot.tmsPersonId !== null && slot.tmsStatus !== "published" && slot.confirmedAt === null,
    ) ?? false;
  return anyDraft ? { variant: "warn", label: "Appointing" } : { variant: "good", label: "Appointed" };
}

/**
 * RUN 82J — the part of §6's pill that is the match's own state: `FT` by the desk's one played
 * rule, or TMS's period while TMS calls the match live; null for a match that is neither.
 *
 * Lifted out of {@link deskStatus} for the third QA's M3: a Today's Matches row whose day has not
 * been read prints this and no appointment pill — `Scheduled` is what an empty index says of a crew
 * nobody has read, and `FT` or `Q3` are true whatever the appointments hold.
 */
export function matchOwnStatus(fixture: FixtureRowView, now: Date): DeskStatus | null {
  if (matchCompleted(fixture, fixture.tmsStatus, now)) {
    return { variant: "muted", label: "FT" };
  }
  if (fixture.tmsStatus === "live") {
    return { variant: "live", label: fixture.period ?? "Live" };
  }
  return null;
}

/* ────────────────────────────────────────────────────────────────────────────
 * 6. The day strip — §5
 * ──────────────────────────────────────────────────────────────────────────── */

export interface DayStripEntry {
  day: string;
  /** `SEP` / `OCT`. */
  monthLabel: string;
  /** Zero-padded day of month. */
  dayLabel: string;
  hasMen: boolean;
  hasWomen: boolean;
  hasHearing: boolean;
  hasFixtures: boolean;
  isPast: boolean;
  ariaLabel: string;
}

/**
 * One button per calendar day between the first and last fixture, inclusive — **not** one per
 * day that has a fixture.
 *
 * §5 calls rest days *"first-class entries"* rather than the absence of fixtures. This build
 * has no `Rest Days` configuration field to read (Configuration §03 does not model one — see
 * the Task 3 report), so a day inside the window with nothing on it is drawn as a day with no
 * fixtures and labelled as such. It is not labelled `rest day`, because that would be
 * asserting a configuration decision nobody has taken.
 */
export function dayStrip(
  fixtures: readonly FixtureRowView[],
  hearings: readonly HearingCaseView[],
  today: string,
): DayStripEntry[] {
  const days = matchDays(fixtures);
  const first = days[0];
  const last = days[days.length - 1];
  if (first === undefined || last === undefined) return [];

  // RUN 82H — the day on Tokyo's clock, not the UTC day the stored instant is written in.
  const hearingDays = new Set(
    hearings
      .map((hearing) =>
        hearing.hearingAt === null ? null : (tournamentDayAndTime(hearing.hearingAt)?.day ?? null),
      )
      .filter((day): day is string => day !== null),
  );

  const entries: DayStripEntry[] = [];
  for (let day = first; day <= last; day = addDays(day, 1)) {
    const onDay = fixtures.filter((fixture) => fixture.date === day);
    const hasMen = onDay.some((fixture) => fixture.competitionGender === "m");
    const hasWomen = onDay.some((fixture) => fixture.competitionGender === "w");
    const hasHearing = hearingDays.has(day);
    const parts: string[] = [];
    if (hasMen) parts.push("Men's matches");
    if (hasWomen) parts.push("Women's matches");
    if (hasHearing) parts.push("DC hearing");
    const date = utc(day);
    entries.push({
      day,
      monthLabel: (MONTHS_SHORT[date?.getUTCMonth() ?? 0] ?? "").toUpperCase(),
      dayLabel: String(date?.getUTCDate() ?? "").padStart(2, "0"),
      hasMen,
      hasWomen,
      hasHearing,
      hasFixtures: onDay.length > 0,
      isPast: day < today,
      ariaLabel: `${dayMonth(day)} — ${parts.length === 0 ? "no fixtures" : parts.join(", ")}`,
    });
  }
  return entries;
}

/**
 * §5's note line, and only its variants — every string below is the spec's, verbatim, with
 * the day and the count substituted.
 */
export function dayNote(input: {
  selected: string | null;
  today: string;
  fixturesOnDay: number;
  pageOfLookahead: number | null;
  filtered: boolean;
  windowFirst: string;
  windowLast: string;
}): string {
  if (input.selected === null) {
    return (
      `Timeline · ${dayMonth(input.windowFirst)} – ${dayMonth(input.windowLast)} · Dots: ` +
      `Navy = Men's · Pink = Women's · Orange = DC Hearing · Click a day to jump to it in ` +
      `Tomorrow Onwards`
    );
  }
  if (input.selected === input.today) {
    return `${dayMonth(input.selected)} is today — see Today's Matches above.`;
  }
  if (input.fixturesOnDay === 0) {
    return input.filtered
      ? `${shortDate(input.selected)} — no fixtures under the current competition filter.`
      : `${shortDate(input.selected)} — no fixtures.`;
  }
  const page = input.pageOfLookahead;
  const where = page === null ? "not shown under Tomorrow Onwards" : `shown on page ${String(page)} of Tomorrow Onwards`;
  return (
    `${shortDate(input.selected)} — ${String(input.fixturesOnDay)} ` +
    `${input.fixturesOnDay === 1 ? "fixture" : "fixtures"} · ${where}.`
  );
}

/* ────────────────────────────────────────────────────────────────────────────
 * 7. Appointment Status — §9
 * ──────────────────────────────────────────────────────────────────────────── */

export interface ApptStatus {
  complete: number;
  total: number;
  percent: number;
  conflicts: string[];
  incomplete: string[];
}

/**
 * §9's count-up figure and its three check rows, over the days this page actually holds.
 *
 * RUN 82J — the page hands it the fixtures of the days {@link appointmentWindow} counts, and no
 * others: two fixed days, each once its own read has landed.
 *
 * The mockup's caption is `of 40 matches / fully appointed`. It is qualified here — the count
 * is of **technical** crews — because that is the only completeness this build can assert:
 * the umpiring half of every crew is module 02 and has no store, so a figure that claimed
 * whole crews would be counting three slots and calling them seven.
 */
export function appointmentStatus(
  fixtures: readonly FixtureRowView[],
  appointed: ReadonlyMap<number, AppointmentFixtureBody>,
): ApptStatus {
  const conflicts: string[] = [];
  const incomplete: string[] = [];
  let complete = 0;
  for (const fixture of fixtures) {
    const record = appointed.get(fixture.tmsMatchDbId) ?? null;
    if (hasCountryConflict(record)) conflicts.push(fixture.displayMatchNo);
    if (technicalComplete(record)) complete += 1;
    else incomplete.push(fixture.displayMatchNo);
  }
  return {
    complete,
    total: fixtures.length,
    percent: fixtures.length === 0 ? 0 : Math.round((complete / fixtures.length) * 100),
    conflicts,
    incomplete,
  };
}

/** RUN 82J — §9's two-day window, and where each of its days' own reads stands. */
export interface AppointmentWindow {
  /** The window: the focus day, then the next match day — whichever of the two exist. */
  days: string[];
  /** The window's days whose own appointments read has landed. The only days the figure counts. */
  counted: string[];
  /** Asked, and never yet answered: not counted, and not a failure either. */
  loading: string[];
  /** Refused, and never answered: not counted, and the panel says so. */
  refused: string[];
  /** The head note — the days counted, or the window's days while none is. */
  label: string;
}

/**
 * RUN 82J — which days Appointment Status counts: **the focus day and the next match day, each once
 * its own appointments have been read** (third QA, L5 and M3, 2026-09-18).
 *
 * **A fixed window (L5).** RUN 82G counted the days whose appointments the desk held, so that a page
 * turn would stop dipping the figure. But what the desk holds still followed the pager — five match
 * days on the first page, one while the next page loaded, and whatever that page held once it
 * landed — so the figure read `6 of 6 · 100%` and then `6 of 30 · 20%` for the same crews. Its window
 * is now two fixed days, the focus day and the next match day ({@link nextMatchDay}), and nothing the
 * lookahead does can move them.
 *
 * **A day nobody has read is not counted (M3).** An empty index reads as a day of unappointed
 * matches, so a day whose read is still out, or was refused, counted every match on it incomplete.
 * Now it is left out until its read lands, and a refusal is named in `refused` for the panel to say.
 *
 * The head note names the days the figure counts — `Fri 18 Sep + Sat 19 Sep`, or `Sat 19 Sep` alone
 * while the focus day is left out — so the caption is always true of the figure beneath it.
 */
export function appointmentWindow(
  entries: readonly { day: string | null; read: DayReadState }[],
): AppointmentWindow {
  const window = entries.filter(
    (entry): entry is { day: string; read: DayReadState } => entry.day !== null,
  );
  const days = window.map((entry) => entry.day);
  const counted = window.filter((entry) => entry.read === "read").map((entry) => entry.day);
  const named = counted.length > 0 ? counted : days;
  return {
    days,
    counted,
    loading: window.filter((entry) => entry.read === "loading").map((entry) => entry.day),
    refused: window.filter((entry) => entry.read === "failed").map((entry) => entry.day),
    label: named.length === 0 ? "No Match Days" : named.map(shortDate).join(" + "),
  };
}

/**
 * §9's third check row — a crew flagged against a knockout placeholder.
 *
 * The row exists because a placeholder team may still resolve to an official's own country,
 * and until it does the conflict cannot be ruled out. What can be stated from the data this
 * build holds is exactly that: which appointed fixtures still have an unresolved side. The
 * mockup's sentence names the country it suspects; that would need the pool table, which no
 * route serves, so the sentence here names the fixture and the position instead.
 */
export function provisionalCrews(
  fixtures: readonly FixtureRowView[],
  appointed: ReadonlyMap<number, AppointmentFixtureBody>,
): { matchNo: string; sides: string }[] {
  return fixtures
    .filter(
      (fixture) =>
        (fixture.homeTeamCode === null || fixture.awayTeamCode === null) &&
        technicalFilled(appointed.get(fixture.tmsMatchDbId) ?? null) > 0,
    )
    .map((fixture) => ({
      matchNo: fixture.displayMatchNo,
      sides: `${fixture.homeTeamCode ?? fixture.homePlaceholder ?? "?"} v ${
        fixture.awayTeamCode ?? fixture.awayPlaceholder ?? "?"
      }`,
    }));
}

/* ────────────────────────────────────────────────────────────────────────────
 * 8. Tournament Officials donut — §9
 * ──────────────────────────────────────────────────────────────────────────── */

/** §9's six categories and their fixed colours. `TDs` is plural: a big event may have two. */
export const ROLE_SEGMENTS: readonly { role: string; label: string; colour: string }[] = [
  { role: "TD", label: "TDs", colour: "#c93a5b" },
  { role: "ATD", label: "ATDs", colour: "#c07a10" },
  { role: "TO", label: "TOs", colour: "#0b9488" },
  { role: "Judge", label: "Judges", colour: "#8248c9" },
  { role: "UM", label: "UMs", colour: "#4f8ed3" },
  { role: "Umpire", label: "Umpires", colour: "#2d5f9e" },
];

export interface DonutSegment {
  label: string;
  colour: string;
  count: number;
  /** `stroke-dasharray` and `stroke-dashoffset` for an r=15.9155 circle (circumference 100). */
  dash: string;
  offset: number;
}

export interface DonutView {
  total: number;
  segments: DonutSegment[];
  ariaLabel: string;
}

export function officialsDonut(officials: readonly OfficialView[]): DonutView {
  const counts = ROLE_SEGMENTS.map((segment) => ({
    ...segment,
    count: officials.filter((official) => official.role === segment.role).length,
  }));
  const total = counts.reduce((sum, entry) => sum + entry.count, 0);
  let cursor = 0;
  const segments = counts.map((entry): DonutSegment => {
    const share = total === 0 ? 0 : (entry.count / total) * 100;
    const segment: DonutSegment = {
      label: entry.label,
      colour: entry.colour,
      count: entry.count,
      dash: `${share.toFixed(2)} ${(100 - share).toFixed(2)}`,
      offset: -cursor,
    };
    cursor += share;
    return segment;
  });
  const spoken = counts
    .filter((entry) => entry.count > 0)
    .map((entry) => `${String(entry.count)} ${entry.label}`)
    .join(", ");
  return {
    total,
    segments,
    ariaLabel:
      total === 0
        ? "Tournament officials by role: no roster loaded"
        : `Tournament officials by role: ${spoken}`,
  };
}

/* ────────────────────────────────────────────────────────────────────────────
 * 9. Pool Stage Progress — §9
 * ──────────────────────────────────────────────────────────────────────────── */

export interface PoolProgress {
  poolComplete: number;
  poolTotal: number;
  knockoutComplete: number;
  knockoutTotal: number;
  /** Eight bar heights, 0–100, one per eighth of the pool stage. */
  bars: number[];
}

/**
 * A fixture is a pool match when its stage names a pool, and a knockout otherwise — which is
 * exactly how the Fixtures page already reads `stage`, and is the only split the fixture
 * record supports.
 *
 * **RUN 82C — `complete` is the desk's one played rule ({@link matchCompleted}).** Counted from
 * the status alone, the figure stood at the first day's Official matches all tournament.
 */
export function poolProgress(fixtures: readonly FixtureRowView[], now: Date): PoolProgress {
  const done = (fixture: FixtureRowView): boolean =>
    matchCompleted(fixture, fixture.tmsStatus, now);
  const pool = fixtures.filter((fixture) => /pool/i.test(fixture.stage));
  const knockout = fixtures.filter((fixture) => !/pool/i.test(fixture.stage));
  const ordered = [...pool].sort(byKickOff);
  const bars: number[] = [];
  for (let index = 0; index < 8; index += 1) {
    const from = Math.floor((ordered.length * index) / 8);
    const to = Math.floor((ordered.length * (index + 1)) / 8);
    const slice = ordered.slice(from, to);
    bars.push(slice.length === 0 ? 0 : Math.round((slice.filter(done).length / slice.length) * 100));
  }
  return {
    poolComplete: pool.filter(done).length,
    poolTotal: pool.length,
    knockoutComplete: knockout.filter(done).length,
    knockoutTotal: knockout.length,
    bars,
  };
}

/* ────────────────────────────────────────────────────────────────────────────
 * 10. Appointment summaries — §10
 * ──────────────────────────────────────────────────────────────────────────── */

export interface WorkloadRow {
  tmsPersonId: string;
  name: string;
  country: string;
  role: string;
  gender: "m" | "f" | null;
  to: number;
  sj: number;
  tj: number;
  total: number;
  /** How many of those appointments are on matches already played. */
  completed: { to: number; sj: number; tj: number; total: number };
}

/**
 * §10's `Tech Table Appointments` rows — **only TOs and Judges**, as the panel states.
 *
 * `completed / appointed` is the cell format the panel fixes, so the completed half has to be
 * counted from the fixtures the appointments sit on rather than from the workload totals,
 * which count appointments and know nothing about whether the match has been played.
 */
export function technicalWorkloadRows(
  workloads: readonly OfficialWorkload[],
  officials: readonly OfficialView[],
  appointed: readonly AppointmentFixtureBody[],
  playedMatchIds: ReadonlySet<number>,
): WorkloadRow[] {
  const completedFor = (tmsPersonId: string): { to: number; sj: number; tj: number; total: number } => {
    const tally = { to: 0, sj: 0, tj: 0, total: 0 };
    for (const fixture of appointed) {
      if (!playedMatchIds.has(fixture.tmsMatchDbId)) continue;
      for (const slot of fixture.slots) {
        if (slot.tmsPersonId !== tmsPersonId) continue;
        if (slot.slot === "TO") tally.to += 1;
        if (slot.slot === "SJ") tally.sj += 1;
        if (slot.slot === "TJ") tally.tj += 1;
        tally.total += 1;
      }
    }
    return tally;
  };

  return officials
    .filter((official) => official.role === "TO" || official.role === "Judge")
    .map((official): WorkloadRow => {
      const workload = workloads.find((entry) => entry.official_id === official.tmsPersonId);
      return {
        tmsPersonId: official.tmsPersonId,
        name: official.mediaName,
        country: official.country,
        role: official.role,
        gender: official.gender,
        to: workload?.to ?? 0,
        sj: workload?.sj ?? 0,
        tj: workload?.tj ?? 0,
        total: workload?.total ?? 0,
        completed: completedFor(official.tmsPersonId),
      };
    })
    .sort((a, b) => b.total - a.total || b.completed.total - a.completed.total || (a.name < b.name ? -1 : 1));
}

/**
 * §10's `Umpires Appointments` row — **only Umpires**, as the panel states.
 *
 * The columns the specification fixes are `FU` / `RU` / `VU`, and the engine's ledger
 * (`UmpireWorkload`) names the same three duties `on_field` / `reserve` / `video`. §B6 is
 * explicit that the three are never summed *for the engine's own evenness reasoning*; §10 is
 * equally explicit that this panel carries a `Total` column, so the total is computed here,
 * for display, and is never fed back into anything that reasons about balance.
 *
 * **The Umpires Managers' half, never the advisory one.** The appointments a Technical
 * Delegate is owed a workload of are the real ones (`tmsActual`, §B1/§B4). The AI half is a
 * yardstick that is never pushed (§B9), and counting it here would print duties nobody holds.
 */
export interface UmpireWorkloadRow {
  tmsPersonId: string;
  name: string;
  country: string;
  gender: "m" | "f" | null;
  fu: number;
  ru: number;
  vu: number;
  total: number;
  /** How many of those appointments are on matches already played. */
  completed: { fu: number; ru: number; vu: number; total: number };
}

export function umpireWorkloadRows(
  workloads: readonly UmpireWorkload[],
  officials: readonly OfficialView[],
  appointed: readonly UmpireFixtureBody[],
  playedMatchIds: ReadonlySet<number>,
): UmpireWorkloadRow[] {
  const completedFor = (
    tmsPersonId: string,
  ): { fu: number; ru: number; vu: number; total: number } => {
    const tally = { fu: 0, ru: 0, vu: 0, total: 0 };
    for (const fixture of appointed) {
      if (!playedMatchIds.has(fixture.tmsMatchDbId)) continue;
      for (const slot of fixture.tmsActual) {
        if (slot.tmsPersonId !== tmsPersonId) continue;
        if (slot.slot === "U1" || slot.slot === "U2") tally.fu += 1;
        if (slot.slot === "RU") tally.ru += 1;
        if (slot.slot === "VU") tally.vu += 1;
        tally.total += 1;
      }
    }
    return tally;
  };

  return officials
    .filter((official) => official.role === "Umpire")
    .map((official): UmpireWorkloadRow => {
      const workload = workloads.find((entry) => entry.official_id === official.tmsPersonId);
      const fu = workload?.on_field ?? 0;
      const ru = workload?.reserve ?? 0;
      const vu = workload?.video ?? 0;
      return {
        tmsPersonId: official.tmsPersonId,
        name: official.mediaName,
        country: official.country,
        gender: official.gender,
        fu,
        ru,
        vu,
        total: fu + ru + vu,
        completed: completedFor(official.tmsPersonId),
      };
    })
    .sort(
      (a, b) =>
        b.total - a.total || b.completed.total - a.completed.total || (a.name < b.name ? -1 : 1),
    );
}

/** §10's gender colour class. `g-u` when the intake has not supplied one — see the stylesheet. */
export function genderClass(gender: "m" | "f" | null): string {
  if (gender === "m") return "g-m";
  if (gender === "f") return "g-f";
  return "g-u";
}

/* ────────────────────────────────────────────────────────────────────────────
 * 11. Alerts — §9, and gap N4
 * ──────────────────────────────────────────────────────────────────────────── */

export type AlertUrgency = "u-alert" | "u-warn" | "u-info";

export interface DeskAlert {
  /** Stable across refetches, so an alert marked done stays done (gap N4). */
  id: string;
  urgency: AlertUrgency;
  /** The bold lead of the line. */
  lead: string;
  /** The rest of it. */
  rest: string;
  /** The right-hand stamp: `TODAY`, `SEP 19`, `08:12`. */
  when: string;
  /** Where the alert's own record lives, so it can be opened (gap N4). */
  href: string | null;
  hrefLabel: string;
}

const URGENCY_ORDER: Readonly<Record<AlertUrgency, number>> = {
  "u-alert": 0,
  "u-warn": 1,
  "u-info": 2,
};

/**
 * `TODAY` for today, otherwise `SEP 19`.
 *
 * RUN 82J — today on the tournament's clock, and never the focus day (third QA, H1): on a day with
 * no fixtures the focus day is the next match day, and its crew alerts were stamped `TODAY`.
 */
function stamp(day: string, today: string): string {
  return day === today ? "TODAY" : dayMonth(day).toUpperCase().replace(" ", " ");
}

/**
 * The alerts this build can actually derive, sorted by urgency.
 *
 * §9 lists eight switchable alert types in Configuration §07. Four of them are derivable from
 * routes this build has — an unfilled appointment slot, a country conflict, a hearing
 * scheduled today, and reports not yet drafted. The other four are not, and each is absent
 * rather than approximated:
 *
 *   · **Official Birthday** — `GET /officials` carries no date of birth (only the per-official
 *     detail read does), and §9's rule needs the whole roster's. Fetching sixty profiles to
 *     draw a cake is not a trade this page should make; the alert wants a roster-wide field.
 *   · **Team Colours Missing** — the fixture's `colmiss` status is on the Fixtures page and is
 *     surfaced here through the fixture status, not as a second alert.
 *   · **TMS Fetch Failed** — no fetch loop exists in this build to fail.
 *   · **Marks Deadline Approaching** — Configuration §07's `Daily Marks Deadline` is not a
 *     field the configuration store holds, so there is no deadline to approach.
 *
 * There is also no switch to read: §07's `Alerts` toggles are not modelled either. Every
 * alert below is therefore on, and the panel says so in its own foot.
 *
 * **RUN 82C.** A played match raises no crew alert, and `played` is the desk's one rule
 * ({@link matchCompleted}) — read against `now`, the page's minute. Read from the status alone, an
 * unfilled slot on a match finished an hour ago went on asking to be filled, because the mirrored
 * status of that match never moved.
 *
 * **RUN 82G — no crew alert before the crews are read (second QA, L11).** `crewsRead` says whether
 * the focus day's own appointments are held. Until they are, `appointed` knows nothing of that
 * day, and every match on it looked unfilled: the strip at the top of the page opened on a false
 * `Unfilled appointment` while the read was still out, and went on saying it for as long as a
 * refused read stayed refused. So while `crewsRead` is false neither the unfilled-slot alert nor
 * the country-conflict alert is raised — both are questions about crews nobody has read — and the
 * page says the crews are still being read instead. The hearing and report alerts do not read the
 * crews and are unaffected.
 *
 * **RUN 82J — `today` is the tournament's today, and the focus day is only where the crews come
 * from (third QA, H1).** The page passed the focus day as `today`, so on a day with no fixtures —
 * when the focus day falls forward to the next match day — a hearing held that day was measured
 * against tomorrow and dropped as past, and tomorrow's hearing was called `today`. Every `today`,
 * `tomorrow` and `TODAY` below is now measured against the real day, on Tokyo's clock. The crew
 * alerts are still about the focus day's matches, which is what the desk reads the crews of, and are
 * stamped with that day's own date when it is not today; the unfilled-slot line names the day of its
 * push-back too, since the strip at the top of the page prints the line without the stamp.
 *
 * **RUN 82J — the reports alert counts in words that agree (L11):** `1 official`, `1 of 2 is`.
 */
export function deskAlerts(input: {
  /** RUN 82J — today on the tournament's clock (`todayIso`). Never the focus day. */
  today: string;
  /** The focus day's fixtures — today's, or the next match day's when today has none. */
  focusFixtures: readonly FixtureRowView[];
  appointed: ReadonlyMap<number, AppointmentFixtureBody>;
  /** RUN 82G — whether the focus day's appointments have been read. Required, never assumed. */
  crewsRead: boolean;
  hearings: readonly HearingCaseView[];
  reportsOutstanding: number;
  reportsTotal: number;
  now: Date;
}): DeskAlert[] {
  const alerts: DeskAlert[] = [];

  for (const hearing of input.hearings) {
    if (hearing.hearingAt === null) continue;
    // RUN 82H — the hearing's day and time as a Tokyo clock reads them (see `tournamentDayAndTime`).
    const at = tournamentDayAndTime(hearing.hearingAt);
    if (at === null) continue;
    const { day, time } = at;
    if (day < input.today) continue;
    const when = daysBetween(input.today, day);
    if (when > 1) continue;
    alerts.push({
      id: `hearing:${hearing.id}`,
      urgency: "u-alert",
      lead: `DC Hearing ${when === 0 ? "today" : "tomorrow"} ${time}`,
      rest: `— case ${hearing.caseNumber}${hearing.teamCode === null ? "" : `, ${hearing.teamCode}`}; open the case file to review it.`,
      when: stamp(day, input.today),
      href: "/hearings",
      hrefLabel: "Open in Disciplinary Hearings",
    });
  }

  for (const fixture of input.crewsRead ? input.focusFixtures : []) {
    const record = input.appointed.get(fixture.tmsMatchDbId) ?? null;
    if (matchCompleted(fixture, fixture.tmsStatus, input.now)) continue;
    if (hasCountryConflict(record)) {
      alerts.push({
        id: `conflict:${String(fixture.tmsMatchDbId)}`,
        urgency: "u-alert",
        lead: `Country conflict — ${fixture.displayMatchNo}`,
        rest: `— an appointed official's own country is in this match. Quick check is blocked until it is resolved.`,
        when: stamp(fixture.date, input.today),
        href: "/appointments/technical",
        hrefLabel: "Resolve in TO Appointments",
      });
    }
    const filled = technicalFilled(record);
    if (filled < TECHNICAL_SLOTS.length) {
      alerts.push({
        id: `unfilled:${String(fixture.tmsMatchDbId)}`,
        urgency: "u-warn",
        lead: `Unfilled appointment — ${fixture.displayMatchNo}`,
        rest: `— ${String(TECHNICAL_SLOTS.length - filled)} of ${String(TECHNICAL_SLOTS.length)} technical slots still to fill · push-back ${
          fixture.date === input.today ? fixture.time : `${shortDate(fixture.date)} ${fixture.time}`
        }.`,
        when: stamp(fixture.date, input.today),
        href: "/appointments/technical",
        hrefLabel: "Fill in TO Appointments",
      });
    }
  }

  if (input.reportsOutstanding > 0) {
    const underWay = input.reportsTotal - input.reportsOutstanding;
    alerts.push({
      id: "reports:outstanding",
      urgency: "u-warn",
      lead: `Reports not yet drafted for ${countOf(input.reportsOutstanding, "official")}`,
      rest: `— ${String(underWay)} of ${String(input.reportsTotal)} ${underWay === 1 ? "is" : "are"} under way.`,
      when: "TODAY",
      href: "/marking",
      hrefLabel: "Open Reporting",
    });
  }

  return alerts.sort(
    (a, b) => URGENCY_ORDER[a.urgency] - URGENCY_ORDER[b.urgency] || (a.id < b.id ? -1 : 1),
  );
}

/* ────────────────────────────────────────────────────────────────────────────
 * 12. The hero countdown — §4
 * ──────────────────────────────────────────────────────────────────────────── */

/**
 * The next fixture that has not been played, in kick-off order.
 *
 * **RUN 82C — `played` is the desk's one rule ({@link matchCompleted}).** Read from the status
 * alone, the hero's `Next push-back` named the day's first match all day on the live system — as
 * `Under way`, because its mirrored status never left `published`. The clock moves it on at
 * kick-off plus the match length, or at the end of a `live` status's hold.
 */
export function nextFixture(
  fixtures: readonly FixtureRowView[],
  today: string,
  now: Date,
): FixtureRowView | null {
  return (
    [...fixtures]
      .filter(
        (fixture) =>
          fixture.date >= today && !matchCompleted(fixture, fixture.tmsStatus, now),
      )
      .sort(byKickOff)[0] ?? null
  );
}

/**
 * §4's `T−HH:MM:SS`.
 *
 * Null once the kick-off has passed, which is what the hero needs in order to stop counting
 * down to a match that has started rather than showing a negative clock. Hours are not capped
 * at 24: a countdown to tomorrow's opener reads `T−31:12:04`, which is honest, where
 * `T−07:12:04` would be wrong by a day.
 *
 * Both sides are wall-clock readings written down as if they were UTC — the fixture's own date and
 * time, and the clock's date and time now — so the difference between them is the time to kick-off
 * whatever zone they were read in, as long as both were read in the same one.
 *
 * **RUN 82G — the same one is the tournament's (second QA, M1).** The clock side was the DEVICE's
 * (`now.getHours()` and its siblings), and the kick-off is a time in Tokyo: on a laptop set to UTC
 * the hero counted down nine hours too far, and said `Under way` nowhere near a kick-off. Now it
 * reads the tournament's clock (`tournamentClockParts`, lifted to `app/tournamentTime.ts` in
 * RUN 83).
 */
export function countdown(fixture: FixtureRowView, now: Date): string | null {
  const kickOff = new Date(`${fixture.date}T${fixture.time}:00Z`);
  if (Number.isNaN(kickOff.getTime())) return null;
  const clock = tournamentClockParts(now);
  const nowOnClock = Date.UTC(
    clock.year,
    clock.month - 1,
    clock.day,
    clock.hour,
    clock.minute,
    clock.second,
  );
  const seconds = Math.floor((kickOff.getTime() - nowOnClock) / 1000);
  if (seconds < 0) return null;
  const pad = (value: number): string => String(value).padStart(2, "0");
  return `T−${pad(Math.floor(seconds / 3600))}:${pad(Math.floor(seconds / 60) % 60)}:${pad(seconds % 60)}`;
}

/**
 * The set of match ids whose match has been played — used by the `completed / appointed` cells.
 *
 * **RUN 82C — by the desk's one rule ({@link matchCompleted}).** From the status alone, §10's
 * `completed` half stayed at the Official matches while §8b beside it counted every match the
 * clock had finished; a TO read `0/1` in one panel and `1` in the other for the same match.
 */
export function playedMatchIds(fixtures: readonly FixtureRowView[], now: Date): Set<number> {
  return new Set(
    fixtures
      .filter((fixture) => matchCompleted(fixture, fixture.tmsStatus, now))
      .map((fixture) => fixture.tmsMatchDbId),
  );
}

/** Every appointment record this page holds, keyed by the fixture it belongs to. */
export function indexAppointments(
  days: readonly { fixtures: readonly AppointmentFixtureBody[] }[],
): Map<number, AppointmentFixtureBody> {
  const index = new Map<number, AppointmentFixtureBody>();
  for (const day of days) {
    for (const fixture of day.fixtures) index.set(fixture.tmsMatchDbId, fixture);
  }
  return index;
}

/** A slot's own country, for the crew sheet's nationality line. */
export function slotCountry(slot: AppointmentSlotBody): string {
  return slot.country ?? "—";
}

/* ────────────────────────────────────────────────────────────────────────────
 * 13. RUN 82 — the lock on the desk: the crew line's chip, and the locked record
 * ──────────────────────────────────────────────────────────────────────────── */

/**
 * RUN 82 (the Technical Delegate's Command Desk addendum, approved 17 September 2026) — what a
 * match's TO, SJ and TJ are to TMS, in the three words the desk prints for it.
 *
 * It is read straight off the §A11 lock the TO Appointments page writes, and it asks the same
 * two questions in the same order that page's status roll-up does (`FixtureListPanel`'s
 * `fixtureStatus`): **is the crew locked** — `On TMS`, the record — and, failing that, **was it
 * reopened for a correction TMS does not hold yet** — `Needs Re-Push`. Everything else is a
 * `Draft`, because the lock is the one statement that the crew is in TMS, and without it no
 * amount of drafting or signing off says so. The two cannot both be true of one match: `locked`
 * is all three slots locked, and a locked slot never carries `needsPush`.
 */
export type CrewLockState = "on_tms" | "needs_push" | "draft";

export interface CrewLine {
  /** TO, SJ and TJ in §6's order — the appointed official's name, or null where unfilled. */
  slots: { slot: TechnicalSlot; name: string | null }[];
  state: CrewLockState;
}

/**
 * §6's crew line — `TO OKONKWO Ada · SJ LINDQVIST Bo · TJ RAMIREZ Pilar` and its chip — or
 * **null for a match with no technical crew at all**.
 *
 * The null is the honest reading of an unappointed match and the reason this is not simply
 * `crewSheet` again: a `Draft` chip under three empty slots would claim a draft nobody has
 * made. So a match with nothing in it keeps exactly what its row already said — the crew
 * glance's `TO, SJ, TJ Pending` — and the line appears with the first appointment. A locked
 * or reopened match always draws it, whatever its slots hold, because its chip is a fact
 * about TMS the TD must see.
 */
export function crewLine(appointed: AppointmentFixtureBody | null): CrewLine | null {
  if (appointed === null) return null;
  if (technicalFilled(appointed) === 0 && !appointed.locked && !appointed.needsPush) return null;
  return {
    slots: TECHNICAL_SLOTS.map((slot) => {
      const entry = appointed.slots.find(
        (candidate) => candidate.slot === slot && candidate.tmsPersonId !== null,
      );
      if (entry === undefined || entry.tmsPersonId === null) return { slot, name: null };
      return { slot, name: entry.mediaName ?? entry.tmsPersonId };
    }),
    state: appointed.locked ? "on_tms" : appointed.needsPush ? "needs_push" : "draft",
  };
}

/*
 * RUN 82's `matchCompleted` stood here, written for the locked record alone. RUN 82C made it the
 * desk's one played rule and moved it to section 2a, beside the calendar every other figure is
 * read against; it is the same function, with the `live` / `warmup` hold added.
 */

/** One row of §8b's tally — one official, what they worked on the locked record, by role. */
export interface LockedTallyRow {
  tmsPersonId: string;
  name: string;
  country: string;
  to: number;
  sj: number;
  tj: number;
  total: number;
}

/** §8b's `Actual Appointments — Completed`, every figure of it. */
export interface LockedRecord {
  /** One row per official with at least one locked appointment on a played match. */
  rows: LockedTallyRow[];
  /** `Y` — matchdays with at least one played match. */
  matchdays: number;
  /** `X` — of those, the matchdays whose played matches are ALL locked. */
  matchdaysLocked: number;
  /** `N` — played matches whose TO, SJ and TJ are locked. */
  matches: number;
  /** `M` — locked appointments on played matches. Always the sum of the rows' totals. */
  appointments: number;
  /** Locked matches not yet played — what the empty state says on a Day 1 morning. */
  lockedToCome: number;
  /**
   * RUN 82C — played matches whose crew is NOT locked. What the empty state leads with: a
   * played match the Technical Delegate has not locked is something he can act on now, where a
   * locked match still to come only has to wait.
   *
   * RUN 82G — always `playedReadyToLock + playedNotSignedOff`. The empty state no longer reads
   * this sum, because only the first half is something a lock press can settle.
   */
  playedUnlocked: number;
  /**
   * RUN 82G — played, not locked, and **signed off** (`confirmed`): the matches `Mark Day Locked`
   * would lock now. The only ones the empty state tells the Technical Delegate to lock.
   */
  playedReadyToLock: number;
  /**
   * RUN 82G — played, not locked, and **not signed off**: nobody appointed, a crew drafted but never
   * signed off (a match reopened with `Unlock` is one — the unlock clears its sign-off), or an
   * Official match TMS froze before its crew was signed off here, which can no longer be.
   */
  playedNotSignedOff: number;
}

/**
 * RUN 82 — §8b's locked record: who actually officiated, as TMS holds it.
 *
 * **The Technical Delegate's counting rule: locked appointments only, never drafts.** A slot
 * counts when its day has been marked locked (§A11) and its match has been played
 * ({@link matchCompleted}); an appointment that is drafted, signed off but not locked, or
 * reopened with `Unlock`, is not the record and is not counted, however complete it looks.
 *
 * **Counted from the slots, not multiplied from the matches.** The mockup printed its
 * appointments figure as matches × 3. Under §A11 that is the same number — a match's three
 * slots are locked together and unlocked together — but counting the slots means the strip can
 * never state an appointment the table does not list: `appointments` is the sum of the rows'
 * totals by construction.
 *
 * `appointed` is the schedule-wide `GET /appointments` body's fixtures; `schedule` is `GET
 * /fixtures`, read here only for each match's status. A match the schedule read does not carry
 * falls back to its own `frozen` flag, which is `official` by another name, and to the clock —
 * so a refused schedule read cannot empty the record.
 *
 * **RUN 82G — the schedule's row decides WHEN, as well as the status (second QA, L1).** The record
 * read its match's date and kick-off off the appointments body, and let the body's `frozen` settle
 * `played` ahead of the schedule's status — while every other figure on the desk reads all three
 * off the fixtures read. Two reads taken minutes apart can disagree (a kick-off moved, a match gone
 * Official in between), and the record then counted a match the dateline beside it did not. So
 * where the schedule read carries the match, its date, its kick-off and its status are the ones
 * asked, and `frozen` is not consulted for `played` at all; the body's own date, kick-off and
 * `frozen` are the fallback for a match the schedule read does not carry, and only for that. The
 * matchday a played match counts towards is the schedule's date by the same rule.
 *
 * **RUN 82G — the played matches that are not locked, split in two (second QA, L6).** The empty
 * state told the Technical Delegate to lock every one of them, and a lock press settles only a
 * crew that is signed off (`POST /appointments/lock` refuses a day holding an unsettled crew, and
 * leaves an Official match that was never signed off as TMS holds it). So `playedReadyToLock` counts
 * the signed-off ones and `playedNotSignedOff` the rest. **An Official match that WAS signed off is
 * ready to lock**: the route locks it like any other, because that crew is the record (RUN 82A) —
 * and matches go Official on the day they are played, which is exactly when the day gets locked.
 *
 * Rows sort by `Total` descending, then by name, exactly as the mockup's script does.
 */
export function lockedRecord(
  appointed: readonly AppointmentFixtureBody[],
  schedule: readonly FixtureRowView[],
  now: Date,
): LockedRecord {
  const scheduled = new Map(schedule.map((fixture) => [fixture.tmsMatchDbId, fixture]));
  const byOfficial = new Map<string, LockedTallyRow>();
  /** Played matchdays, each mapped to whether every played match on it so far is locked. */
  const days = new Map<string, boolean>();
  let matches = 0;
  let appointments = 0;
  let lockedToCome = 0;
  let playedReadyToLock = 0;
  let playedNotSignedOff = 0;

  for (const fixture of appointed) {
    const onSchedule = scheduled.get(fixture.tmsMatchDbId);
    const played =
      onSchedule === undefined
        ? matchCompleted(fixture, fixture.frozen ? "official" : null, now)
        : matchCompleted(onSchedule, onSchedule.tmsStatus, now);
    if (!played) {
      if (fixture.locked) lockedToCome += 1;
      continue;
    }
    const day = onSchedule?.date ?? fixture.date;
    days.set(day, (days.get(day) ?? true) && fixture.locked);
    if (fixture.locked) matches += 1;
    else if (fixture.confirmed) playedReadyToLock += 1;
    else playedNotSignedOff += 1;

    for (const slot of fixture.slots) {
      // The rule itself: a slot is on the record only once it is locked.
      if (slot.lockedAt === null || slot.tmsPersonId === null) continue;
      appointments += 1;
      const row = byOfficial.get(slot.tmsPersonId) ?? {
        tmsPersonId: slot.tmsPersonId,
        name: slot.mediaName ?? slot.tmsPersonId,
        country: slot.country ?? "—",
        to: 0,
        sj: 0,
        tj: 0,
        total: 0,
      };
      if (slot.slot === "TO") row.to += 1;
      else if (slot.slot === "SJ") row.sj += 1;
      else row.tj += 1;
      row.total += 1;
      byOfficial.set(slot.tmsPersonId, row);
    }
  }

  const rows = [...byOfficial.values()].sort(
    (a, b) =>
      b.total - a.total ||
      a.name.localeCompare(b.name, "en", { numeric: true, sensitivity: "base" }) ||
      (a.tmsPersonId < b.tmsPersonId ? -1 : 1),
  );

  return {
    rows,
    matchdays: days.size,
    matchdaysLocked: [...days.values()].filter((allLocked) => allLocked).length,
    matches,
    appointments,
    lockedToCome,
    playedUnlocked: playedReadyToLock + playedNotSignedOff,
    playedReadyToLock,
    playedNotSignedOff,
  };
}

/**
 * RUN 82C — the provenance at the end of §8b's strip: `As Of 16:42`.
 *
 * The house convention, as `summaryStamp` (`pages/um/matchDebrief.ts`) sets it out: the clock
 * is the tournament's (Asia/Tokyo, through the same `tokyoClockTime`), and there is no stamp at
 * all when there is no read to date — `null`, rather than the time of the render, because a stamp
 * is a claim about when the figures were read and a render is not a read.
 *
 * `readAt` is when the schedule-wide read the record is drawn from last ANSWERED. The desk asks
 * again every five minutes and whenever its tab comes back into view, and a refresh that fails
 * keeps the last good answer on screen — so the stamp is how the Technical Delegate tells a
 * record read a minute ago from one the venue connection has been failing to refresh since
 * lunch. The per-minute clock still moves the played half of the record in between; the stamp
 * dates the locks.
 */
export function recordStamp(readAt: Date | null): string | null {
  if (readAt === null) return null;
  const clock = tokyoClockTime(readAt.toISOString());
  return clock === null ? null : `As Of ${clock}`;
}

/**
 * RUN 82G — how old the answer under a stamp may be before the stamp says the refresh is failing:
 * **ten minutes**, two of the desk's five-minute refreshes.
 *
 * One refresh that has not landed yet is ordinary — it may simply still be out. Two in a row that
 * have not is not, and a desk that goes on printing `As Of 11:02` at 11:40 without a word asks the
 * Technical Delegate to do the subtraction himself. Spec 01 §15 (gap S4) has the stamp turn amber
 * past a configured threshold; Configuration holds no such field, so this is the threshold.
 */
export const STALE_READ_MINUTES = 10;

/** RUN 82G — an `As Of` stamp, and whether it is old enough to say the refresh is failing. */
export interface ReadStamp {
  /** `As Of 16:42` — {@link recordStamp}'s words. */
  label: string;
  /** More than {@link STALE_READ_MINUTES} have passed since the read under it answered. */
  failing: boolean;
}

/**
 * RUN 82G — the stamp a refreshed panel carries: {@link recordStamp}, plus whether the read under
 * it is more than {@link STALE_READ_MINUTES} old at `now` (second QA, L2).
 *
 * The flag is quiet on purpose. It is not a banner and not an alert: the answer on screen is
 * still the last good one, and nothing about it is wrong except its age, so the stamp says so
 * where the age is already printed. `null` exactly when `recordStamp` is — no answer, no stamp.
 */
export function readStamp(readAt: Date | null, now: Date): ReadStamp | null {
  const label = recordStamp(readAt);
  if (label === null || readAt === null) return null;
  return {
    label,
    failing: now.getTime() - readAt.getTime() > STALE_READ_MINUTES * 60_000,
  };
}
